import PlayerPanel from "@/components/game/PlayerPanel";
import { Flag } from "lucide-react";
import Coin from "@/components/game/Coin";
import { PLAYER_COLORS, PLAYER_ON_COLOR } from "@/lib/api";

// This round's turn order (main game: reverse seats, rotating) with a First player marker.
function TurnOrder({ game }) {
  const n = game.players.length;
  const start = game.round_start_seat ?? 0;
  const order = game.turn_order?.length ? game.turn_order : Array.from({ length: n }, (_, k) => (((start - k) % n) + n) % n);
  return (
    <div data-testid="turn-order" className="flex items-center" style={{ gap: 4 }} aria-label="Turn order this round">
      {order.map((idx, k) => {
        const p = game.players[idx];
        const now = idx === game.current_player_index && (game.phase === "action" || game.phase === "draw");
        return (
          <span
            key={p.id}
            data-testid={`turn-chip-${k}`}
            data-seat={idx + 1}
            data-current={now ? "true" : "false"}
            data-first={k === 0 ? "true" : "false"}
            title={`${k === 0 ? "First player: " : ""}${p.name}${now ? " (playing now)" : ""}`}
            className="relative inline-flex items-center justify-center"
            style={{ width: 20, height: 20, borderRadius: 5, background: PLAYER_COLORS[p.color], color: PLAYER_ON_COLOR[p.color], fontSize: 11, fontWeight: 700, boxShadow: now ? "0 0 0 2px var(--table), 0 0 0 3.5px var(--ink)" : "none", opacity: now || game.phase === "weather" ? 1 : 0.75 }}
          >
            {idx + 1}
            {k === 0 && (
              <span data-testid="first-player-marker" aria-label="First player" className="absolute inline-flex items-center justify-center" style={{ top: -6, right: -6, width: 12, height: 12, borderRadius: 6, background: "var(--card)", boxShadow: "0 0 0 1px var(--line)" }}>
                <Flag size={8} strokeWidth={2.4} color="var(--ink)" />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

// LAYOUT F left column: PLAYERS header (Bank on its right), the active player's expanded card,
// the other players as compact rows (hover / click popover), then `children` (SELECTED CARD).
// online: { myColor, canAct } — card counts come from hand_count.
export default function PlayersColumn({ game, scores, resiliences = null, pendingIncome, showAllHands, onOpenScore, onZoomCard, online = null, waterNotes = null, actionMax = 3, expandedColor = null, infoStyle, children = null }) {
  const order = [...game.players.map((p, i) => ({ p, i }))].sort((a, b) => (a.p.color === expandedColor ? -1 : b.p.color === expandedColor ? 1 : a.i - b.i));
  return (
    <aside data-testid="player-panels" className="min-h-0 h-full flex flex-col overflow-hidden">
      <div className="shrink-0 flex items-center mb-1.5" style={{ height: 22, gap: 8, ...infoStyle }}>
        <span className="section-label">Players</span>
        {game.mode === "main" && <TurnOrder game={game} />}
        <span data-drop-bank="true" data-testid="bank-bucket" className="ml-auto inline-flex items-center" style={{ gap: 6, fontSize: 12.5, color: "var(--ink-3)" }}>
          <span>Bank</span>
          <span data-testid="players-bank-total" className="inline-flex items-center gap-1" style={{ fontWeight: 600, color: "var(--ink-2)" }} title="Card costs and repairs are paid to the bank; round income comes from it">
            <Coin size={15} testid="bank-coin-icon" />
            money
          </span>
        </span>
      </div>
      <div data-testid="players-list" className="shrink-0 flex flex-col" style={{ gap: 3 }}>
        {order.map(({ p, i }) => (
          <PlayerPanel
            key={p.id}
            expanded={p.color === expandedColor}
            player={p}
            seat={i + 1}
            active={!game.game_over && i === game.current_player_index}
            mode={game.mode}
            score={scores?.[p.color]}
            resilience={resiliences?.[p.color]}
            incoming={pendingIncome?.[p.color] || 0}
            onOpenScore={onOpenScore}
            showHand={showAllHands && i !== game.current_player_index}
            onZoomCard={onZoomCard}
            online={online}
            waterNote={waterNotes?.[p.color] || null}
            economy={game.economy?.[p.color] || null}
            brackets={game.city_brackets || null}
            actionMax={actionMax}
            infoStyle={infoStyle}
          />
        ))}
      </div>
      {children}
    </aside>
  );
}
